import { motion } from "framer-motion";
import { ArrowRight, Presentation } from "lucide-react";
import { IMAGES } from "@/data/content";
import { FadeIn } from "@/components/Motion";

const ConceptPanel = ({ side, eyebrow, title, fontClass, image, tint, onEnter, testId, dark }) => (
  <motion.button
    data-testid={testId}
    onClick={onEnter}
    whileHover="hover"
    initial="rest"
    animate="rest"
    className={`group relative flex-1 overflow-hidden text-left ${
      dark ? "bg-[#0F0E0C]" : "bg-[#F7F6F2]"
    } min-h-[300px] md:min-h-0`}
  >
    <motion.div
      variants={{ rest: { scale: 1.05, opacity: 0.35 }, hover: { scale: 1.12, opacity: 0.55 } }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="absolute inset-0"
    >
      <img src={image} alt="" className="h-full w-full object-cover" />
      <div className={`absolute inset-0 ${tint}`} />
    </motion.div>
    <div className="relative z-10 flex h-full flex-col justify-between p-8 md:p-12">
      <span
        className={`font-mono text-[11px] uppercase tracking-[0.3em] ${
          dark ? "text-[#F4B032]" : "text-[#8B7768]"
        }`}
      >
        {eyebrow}
      </span>
      <div>
        <h3
          className={`${fontClass} text-4xl md:text-5xl leading-[1.02] ${
            dark ? "text-[#F1F1F1]" : "text-[#1C1B1A]"
          }`}
        >
          {title}
        </h3>
        <motion.span
          variants={{ rest: { opacity: 0, y: 8 }, hover: { opacity: 1, y: 0 } }}
          transition={{ duration: 0.35 }}
          className={`mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] ${
            dark ? "text-[#F1F1F1]" : "text-[#1C1B1A]"
          }`}
        >
          Enter Concept <ArrowRight className="h-4 w-4" />
        </motion.span>
      </div>
    </div>
    <span
      className={`pointer-events-none absolute bottom-6 right-8 font-mono text-6xl font-bold opacity-10 ${
        dark ? "text-white" : "text-black"
      }`}
    >
      {side}
    </span>
  </motion.button>
);

const PresentationIntro = ({ onSelect }) => (
  <div
    data-testid="presentation-intro-screen"
    className="relative flex min-h-screen flex-col bg-[#0A0908]"
  >
    <div className="grain-overlay" />
    <header className="flex flex-col items-start gap-3 px-6 py-6 md:flex-row md:items-center md:justify-between md:px-12">
      <FadeIn>
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4">
          <img src="/avo-logo.png" alt="AVO Fence & Supply" className="h-8 w-auto" data-testid="intro-avo-logo" />
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">
            Website Redesign Presentation
          </p>
        </div>
      </FadeIn>
      <FadeIn delay={0.15}>
        <p className="hidden font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 md:block">
          Prepared for AVO Fence &amp; Supply
        </p>
      </FadeIn>
    </header>

    <div className="px-6 pb-10 pt-6 text-center md:px-12 md:pt-10">
      <FadeIn delay={0.2}>
        <h1 className="font-condensed text-3xl font-bold uppercase tracking-tight text-[#F1F1F1] md:text-4xl">
          Two Creative Options<span className="text-[#F4B032]">.</span>
        </h1>
      </FadeIn>
    </div>

    <div className="flex flex-1 flex-col gap-[2px] px-6 pb-6 md:flex-row md:px-12 md:pb-12">
      <ConceptPanel
        side="A"
        eyebrow="Concept A"
        title="Dark Editorial"
        fontClass="font-condensed font-bold uppercase tracking-tight"
        image={IMAGES.heroDark}
        tint="bg-gradient-to-t from-[#0F0E0C] via-[#0F0E0C]/60 to-transparent"
        onEnter={() => onSelect("a")}
        testId="intro-view-concept-a-button"
        dark
      />
      <ConceptPanel
        side="B"
        eyebrow="Concept B"
        title="Light Architectural"
        fontClass="font-cormorant font-semibold"
        image={IMAGES.arbor}
        tint="bg-gradient-to-t from-[#F7F6F2] via-[#F7F6F2]/70 to-[#F7F6F2]/20"
        onEnter={() => onSelect("b")}
        testId="intro-view-concept-b-button"
      />
    </div>

    <FadeIn delay={0.9} className="pb-28 text-center">
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-600">
        <Presentation className="mr-2 inline h-3.5 w-3.5" />
        Use the floating switcher anytime to jump between directions
      </p>
    </FadeIn>
  </div>
);

export default PresentationIntro;
